import React, { useState } from "react";
import { Link } from "react-router-dom";
import useWaitlistModal from "./useWaitlistModal";
import Header from "./Header";
import Footer from "./Footer";
import WaitlistModal from "./WaitlistModal";

const Faq = () => {
  const {
    showWaitlistModal,
    formData,
    isSubmitting,
    isSubmitted,
    formError,
    openWaitlistModal,
    closeWaitlistModal,
    handleInputChange,
    handleSubmit,
  } = useWaitlistModal();

  const [activeFilter, setActiveFilter] = useState("all");

  const filters = [
    { id: "all", label: "All questions" },
    { id: "Getting Started", label: "Getting started" },
    { id: "Integrations", label: "Integrations" },
    { id: "Privacy & Access", label: "Privacy & access" },
    { id: "For Sales", label: "For sales" },
  ];

  const FAQCard = ({ id, category, question, children }) => {
    const isVisible = activeFilter === "all" || activeFilter === category;
    if (!isVisible) return null;

    return (
      <article
        id={id}
        className="relative bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 my-3 shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
      >
        <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3 pr-12">
          {question}
        </h3>
        <div className="max-w-3xl text-slate-700 text-base leading-relaxed">
          {children}
        </div>
      </article>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header onOpenWaitlist={openWaitlistModal} />

      <main className="bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          {/* Filter Buttons */}
          <div className="flex flex-wrap gap-3 mb-12">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-4 py-2.5 rounded-full border text-sm font-semibold transition-all ${
                  activeFilter === filter.id
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-900 border-slate-200 hover:border-slate-900 hover:bg-slate-900 hover:text-white"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {/* Getting Started Section */}
          {(activeFilter === "all" || activeFilter === "Getting Started") && (
            <section className="mb-16 scroll-mt-24" id="getting-started">
              <div className="flex items-end justify-between gap-4 mb-6 pb-4 border-b border-slate-900">
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
                  Getting Started
                </h2>
                <span className="text-xs font-bold text-slate-500 whitespace-nowrap">
                  08 questions
                </span>
              </div>

              <FAQCard
                id="what-is-jindo"
                category="Getting Started"
                question="What is Jindo?"
              >
                <p className="mb-3">
                  Jindo is a context intelligence platform that helps sales teams work more effectively. It connects meetings, CRM data, emails, notes, calendar activity, and other business information to build a complete, continuously evolving understanding of every customer relationship.
                </p>
                <p>
                  By turning scattered interactions, including spoken conversations, into actionable context, Jindo helps sales teams respond faster, communicate clearly, personalize interactions, improve follow-through, and stay aligned across the entire customer journey.
                </p>
              </FAQCard>

              <FAQCard
                id="download-get-started"
                category="Getting Started"
                question="How do I download and get started with Jindo?"
              >
                <p className="mb-3">
                  Join the{" "}
                  <button
                    onClick={openWaitlistModal}
                    className="text-jindo-orange font-bold hover:underline cursor-pointer"
                  >
                    Jindo waitlist
                  </button>
                  , and we will provide download instructions and your Jindo login credentials. Jindo is available for Windows and Mac.
                </p>
                <p className="mb-3">
                  After installing the app, sign in and Jindo will work automatically with meeting platforms including common ones such as Zoom, Teams, and Google Meet, as well as your desktop VoIP voice apps. Connect your calendar, email, and CRM to help Jindo build customer context, provide AI-powered insights to your team, and communicate with your prospects.
                </p>
                <p>
                  No specialized technical expertise is required to install or use Jindo.
                </p>
              </FAQCard>

              <FAQCard
                id="information-jindo-works-with"
                category="Getting Started"
                question="What information can Jindo work with?"
              >
                <p className="mb-3">
                  Depending on your connected tools, permissions, plan, product configuration, and corporate policies, Jindo can work with:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Meeting transcripts from note-taking tools</li>
                  <li>Live conversations</li>
                  <li>CRM records</li>
                  <li>Personal notes and follow-up information</li>
                  <li>Emails and calendar activity</li>
                  <li>Other supported business data sources</li>
                </ul>
              </FAQCard>

              <FAQCard
                id="digitize-meetings"
                category="Getting Started"
                question="How does Jindo digitize meetings?"
              >
                <p className="mb-3">
                  Jindo digitizes meetings in one of two ways. First, by ingesting meeting transcripts from common note takers. Second, Jindo transcribes meetings natively via a bot running in the background on the desktop. It does not record or store audio or video.
                </p>
                <p>
                  In both cases, the transcribed notes are processed to create the context and intelligence to assist the business process.
                </p>
              </FAQCard>

              <FAQCard
                id="past-interactions"
                category="Getting Started"
                question="Can Jindo work with past interactions and multiple sources?"
              >
                <p>
                  Yes. Jindo is designed to extract and connect information across people, conversations, sources, and time. Critically, instead of treating every meeting or email as an isolated event, Jindo organizes context around people, teams, accounts, and topics. The richer and broader the underlying data sources, the richer the summarized information delivered to you by Jindo.
                </p>
              </FAQCard>

              <FAQCard
                id="outputs-ask-jindo"
                category="Getting Started"
                question="What does Jindo produce, and can I ask questions about my information?"
              >
                <p className="mb-3">
                  Most critically, Jindo creates a single context from all of your data sources. Once in place, this context is used by Jindo to deliver predetermined outputs including meeting summaries, key decisions, action items, follow-ups, account and relationship context, recurring themes, risks, open questions, and potential opportunities.
                </p>
                <p className="mb-3">
                  Additionally, you can use the AskJindo feature to ask ad hoc questions of this single context and get responses relevant to your question.
                </p>
                <div className="mt-4 p-4 border-l-4 border-jindo-orange bg-orange-50 rounded-r-xl italic text-slate-900">
                  For example: "What are the unresolved issues that Acme has reported to members of our customer support team to date?"
                </div>
              </FAQCard>

              <FAQCard
                id="languages"
                category="Getting Started"
                question="Which languages does Jindo support?"
              >
                <p className="mb-3">
                  On the Jindo platform, conversations are transcribed in Global English by default, but you can specify other primary languages, such as Spanish, French, or German, directly within your session settings.
                </p>
                <p>
                  For global meetings where participants switch between or speak multiple languages, we offer a specialized multilingual model. This feature automatically detects and transcribes multiple languages within the same audio stream in real time, eliminating the need for manual routing or separate language setup.
                </p>
              </FAQCard>

              <FAQCard
                id="request-demo"
                category="Getting Started"
                question="How can I request a demo or get help?"
              >
                <p>
                  Join the{" "}
                  <button
                    onClick={openWaitlistModal}
                    className="text-jindo-orange font-bold hover:underline cursor-pointer"
                  >
                    waitlist
                  </button>{" "}
                  to request a demo, ask product questions, confirm integration availability, or discuss how Jindo could fit your organization's workflow.
                </p>
              </FAQCard>
            </section>
          )}

          {/* Integrations Section */}
          {(activeFilter === "all" || activeFilter === "Integrations") && (
            <section className="mb-16 scroll-mt-24" id="integrations">
              <div className="flex items-end justify-between gap-4 mb-6 pb-4 border-b border-slate-900">
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
                  Integrations
                </h2>
                <span className="text-xs font-bold text-slate-500 whitespace-nowrap">
                  05 questions
                </span>
              </div>

              <FAQCard
                id="calendar-email"
                category="Integrations"
                question="Which calendar and email tools can I connect to?"
              >
                <p className="mb-3">
                  Jindo connects with office products from Google and Microsoft, including Gmail, Google Calendar, Outlook, and Outlook Calendar.
                </p>
                <p>
                  The email products are currently used only to send email at your request, not to read your email. The number of these integrations will increase with time and your requirements.
                </p>
              </FAQCard>

              <FAQCard
                id="crm-tools"
                category="Integrations"
                question="Which CRM tools can I connect to? How does Jindo work with them?"
              >
                <p className="mb-3">
                  Jindo integrates with Salesforce, HubSpot, and Zoho CRM, writing directly to them to create context and record aspects relevant to sales and marketing. Since data fields are filled in automatically, you get a clean, up-to-date CRM system with zero effort from your sales team.
                </p>
                <p>
                  Jindo augments your CRM rather than replacing it, so the rich context Jindo extracts and synthesizes becomes available right within your existing CRM system.
                </p>
              </FAQCard>

              <FAQCard
                id="other-tools"
                category="Integrations"
                question="Can Jindo work with a tool that is not directly integrated?"
              >
                <p>
                  Jindo's capabilities depend on its available integrations and supported data sources. If a specific tool is important to your workflow, contact the Jindo team to confirm whether it is supported or whether an appropriate connection can be considered.
                </p>
              </FAQCard>

              <FAQCard
                id="review-edit-send"
                category="Integrations"
                question="Can I review, edit, and send Jindo's output to my tools?"
              >
                <p className="mb-3">
                  Yes. You are the ultimate arbiter of the contextual data, so you can review and refine all data that Jindo generates before sharing.
                </p>
                <p>
                  Depending on the integration and product configuration, reviewed information can be programmatically transferred to connected systems—for example, customer data can be pushed directly to your CRM, or email can be sent to prospects and team members from within Jindo.
                </p>
              </FAQCard>

              <FAQCard
                id="replace-crm-email"
                category="Integrations"
                question="Does Jindo replace my CRM or email?"
              >
                <p>
                  No. Jindo increases the value your team gets from the CRM, email, calendar, and meeting tools it already uses. Jindo adds a connected context layer so those systems can be informed by the broader history of customer and team interactions.
                </p>
              </FAQCard>
            </section>
          )}

          {/* Privacy & Access Section */}
          {(activeFilter === "all" || activeFilter === "Privacy & Access") && (
            <section className="mb-16 scroll-mt-24" id="privacy-access">
              <div className="flex items-end justify-between gap-4 mb-6 pb-4 border-b border-slate-900">
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
                  Privacy &amp; Access
                </h2>
                <span className="text-xs font-bold text-slate-500 whitespace-nowrap">
                  06 questions
                </span>
              </div>

              <FAQCard
                id="control-access"
                category="Privacy & Access"
                question="Can I control what information Jindo can access?"
              >
                <p>
                  You control which accounts and services you connect to, subject to the permissions provided by those services and your organization's settings. Access is limited to the information provided by you.
                </p>
              </FAQCard>

              <FAQCard
                id="who-access"
                category="Privacy & Access"
                question="Who can access information in Jindo?"
              >
                <p>
                  You control who can see the information you create in Jindo, from keeping context private to sharing it broadly via a URL.
                </p>
              </FAQCard>

              <FAQCard
                id="data-protected"
                category="Privacy & Access"
                question="How is my data protected?"
              >
                <p className="mb-3">
                  Jindo protects data using industry-standard encryption, including TLS 1.2 or higher for data in transit and AES-256 for data at rest. Access is controlled through role-based permissions, and data activity is logged for security and accountability.
                </p>
                <p>
                  Contact the Jindo team to discuss specific compliance or data-retention requirements for your organization.
                </p>
              </FAQCard>

              <FAQCard
                id="delete-data"
                category="Privacy & Access"
                question="Can I delete my data?"
              >
                <p>
                  Yes. You own the data that you generate and can delete the data and content as you desire. Additional data-management options may include disconnecting connected services or requesting account deletion, depending on your workspace configuration and permissions.
                </p>
              </FAQCard>

              <FAQCard
                id="train-models"
                category="Privacy & Access"
                question="Does Jindo use my data to train AI models?"
              >
                <p>
                  No. Jindo does not use your data to train AI models. Your information is used only to provide Jindo's features and services in accordance with the applicable privacy policy and plan terms.
                </p>
              </FAQCard>

              <FAQCard
                id="meeting-consent"
                category="Privacy & Access"
                question="Do I need consent before transcribing a meeting?"
              >
                <p>
                  You are responsible for following applicable federal and state laws, company policies, and participant-consent requirements when transcribing conversations.
                </p>
              </FAQCard>
            </section>
          )}

          {/* For Sales Section */}
          {(activeFilter === "all" || activeFilter === "For Sales") && (
            <section className="mb-16 scroll-mt-24" id="for-sales">
              <div className="flex items-end justify-between gap-4 mb-6 pb-4 border-b border-slate-900">
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
                  For Sales
                </h2>
                <span className="text-xs font-bold text-slate-500 whitespace-nowrap">
                  05 questions
                </span>
              </div>

              <FAQCard
                id="sales-use"
                category="For Sales"
                question="How can sales teams use Jindo?"
              >
                <p>
                  The single, shared context that Jindo creates lets sales teams do their jobs better, faster, and easier—including preparing for meetings, understanding account history, summarizing conversations, identifying follow-ups, and maintaining shared customer context. It helps reduce the time spent searching across scattered notes, emails, meeting summaries, and CRM records.
                </p>
              </FAQCard>

              <FAQCard
                id="meeting-prep"
                category="For Sales"
                question="Can Jindo help me prepare for a customer meeting?"
              >
                <p className="mb-3">
                  Since Jindo brings together all the context that you and members of your team have generated with the client across time—from previous conversations, notes, emails, calendar activity, and CRM records—it is uniquely positioned to help you prepare for customer meetings.
                </p>
                <div className="mt-4 p-4 border-l-4 border-jindo-orange bg-orange-50 rounded-r-xl italic text-slate-900">
                  For example: "Will this customer agree to a Net 30 contract? Who is the real decision maker? How powerful is procurement—a rubber-stamping team or one with real teeth?"
                </div>
              </FAQCard>

              <FAQCard
                id="handoffs-followups"
                category="For Sales"
                question="Can Jindo help with account handoffs and follow-ups?"
              >
                <p className="mb-3">
                  It is the nature of today's business world that your and your customers' teams are constantly changing. Consequently, no single person has full context.
                </p>
                <p>
                  Jindo helps teams query and understand what has happened with an account, what was promised, who was or is involved, and which actions remain open. It also surfaces action items, open questions, and follow-up opportunities so transitions between account owners or team members are more informed and consistent.
                </p>
              </FAQCard>

              <FAQCard
                id="sales-risks"
                category="For Sales"
                question="Can Jindo identify sales risks, objections, and opportunities?"
              >
                <p className="mb-3">
                  When used across teams, Jindo builds a broader context that enables it to find patterns no individual human being can possibly identify.
                </p>
                <p>
                  Jindo surfaces patterns such as repeated objections, unresolved concerns, stalled conversations, missing follow-ups, and potential expansion opportunities across connected interactions. What's critically different is that Jindo's insights support professional judgment; they do not replace it.
                </p>
              </FAQCard>

              <FAQCard
                id="teams-together"
                category="For Sales"
                question="Can managers and teams use Jindo together?"
              >
                <p className="mb-3">
                  Yes. Individuals can use Jindo to organize their own conversations and business information, while teams can build shared context across people, accounts, customers, and workstreams.
                </p>
                <p>
                  Value increases exponentially with your team size and time. Where the appropriate permissions are available, managers can also look for recurring customer themes, account activity, and follow-up gaps across their team. Team visibility remains subject to workspace settings and your permissions.
                </p>
              </FAQCard>
            </section>
          )}
        </div>
      </main>

      <Footer />

      <WaitlistModal
        showWaitlistModal={showWaitlistModal}
        closeWaitlistModal={closeWaitlistModal}
        formData={formData}
        handleInputChange={handleInputChange}
        handleSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        isSubmitted={isSubmitted}
        formError={formError}
      />
    </div>
  );
};

export default Faq;
